/**
 * TokenMonitor - Continuously monitors token expiration and triggers refresh
 *
 * Purpose: Instead of relying on a one-time timeout, this monitors token expiration
 * every 30 seconds and proactively refreshes the token before it expires.
 *
 * This is ADDITIVE - it works alongside existing token refresh logic, not replacing it.
 * If the existing logic works, this simply verifies the token is still valid.
 */

import { jwtDecode } from "jwt-decode"

export class TokenMonitor {
  private checkInterval: NodeJS.Timeout | null = null
  private readonly CHECK_INTERVAL_MS = 30000 // Check every 30 seconds
  private readonly REFRESH_BUFFER_MS = 300000 // Refresh 5 minutes before expiry
  private isMonitoring = false

  /**
   * Start monitoring token expiration
   * @param getToken Function to retrieve current token
   * @param onRefreshNeeded Callback when token needs refresh
   * @param options.getExpiryTime Optional custom expiry source returning an
   *        epoch-ms timestamp (or null when unknown). Use this when the
   *        session expiry does NOT live in a JWT — e.g. the platform's
   *        oidc.auth cookie is base64 JSON with its own `expiry` field,
   *        which is what the backend actually enforces. Without this option
   *        the token from getToken() is decoded as a JWT.
   * @returns Disposer function to stop monitoring
   */
  startMonitoring(
    getToken: () => string | null,
    onRefreshNeeded: () => void,
    options?: { getExpiryTime?: () => number | null }
  ): () => void {
    if (this.isMonitoring) {
      console.warn("[TokenMonitor] Already monitoring, not starting again")
      // No-op disposer: a refused start must not hand back a disposer that
      // kills the ORIGINAL caller's active monitor on cleanup
      return () => {}
    }

    this.isMonitoring = true
    console.log("[TokenMonitor] Starting token expiration monitor")

    const checkToken = () => {
      try {
        let expiryTime: number | null

        if (options?.getExpiryTime) {
          expiryTime = options.getExpiryTime()
        } else {
          const token = getToken()
          if (!token) {
            return // No token yet, skip check
          }
          const decoded = jwtDecode<any>(token)
          expiryTime = decoded.exp * 1000 // Convert to milliseconds
        }

        if (expiryTime === null || isNaN(expiryTime)) {
          return // Expiry unknown, skip check
        }

        const now = Date.now()
        const timeUntilExpiry = expiryTime - now

        // If token expires in less than buffer time, refresh immediately
        if (timeUntilExpiry < this.REFRESH_BUFFER_MS) {
          if (timeUntilExpiry > 0) {
            console.log(
              `[TokenMonitor] Token expiring in ${Math.round(timeUntilExpiry / 1000)}s, refreshing now`
            )
          } else {
            console.warn("[TokenMonitor] Token already expired!")
          }
          onRefreshNeeded()
        } else {
          const minutesLeft = Math.round(timeUntilExpiry / 60000)
          console.log(`[TokenMonitor] Token valid, expires in ${minutesLeft} minutes`)
        }
      } catch (error) {
        console.error("[TokenMonitor] Error checking token:", error)
      }
    }

    // Check immediately
    checkToken()

    // Then check periodically
    this.checkInterval = setInterval(checkToken, this.CHECK_INTERVAL_MS)

    // Return disposer function
    return () => this.stopMonitoring()
  }

  /**
   * Stop monitoring token expiration
   */
  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
    this.isMonitoring = false
    console.log("[TokenMonitor] Stopped token expiration monitor")
  }

  /**
   * Check if monitor is currently active
   */
  isActive(): boolean {
    return this.isMonitoring
  }
}

// Export singleton instance for global use
export const tokenMonitor = new TokenMonitor()
